<script setup lang="ts" generic="TItem, TValue extends string | number | null">
import { EKeyboardKey } from '@/utils/menu.utils';
import { computed, nextTick, useTemplateRef } from 'vue';
import { getKeyboardNavigableListTargetIndex, type TKeyboardNavigableListKey } from './keyboardNavigableList.utils';

type TKeyboardNavigableListItemAttributes = {
  'aria-disabled'?: 'true';
  'data-keyboard-navigable-list-control': '';
  tabindex: -1 | 0;
};

const props = withDefaults(
  defineProps<{
    getKey: (item: TItem, index: number) => number | string;
    getValue: (item: TItem, index: number) => TValue;
    isItemDisabled?: (item: TItem, index: number) => boolean;
    items: readonly TItem[];
    modelValue: TValue | null;
    wrap?: boolean;
  }>(),
  { isItemDisabled: undefined, wrap: true },
);

const emit = defineEmits<{
  'update:modelValue': [value: TValue];
}>();

defineSlots<{
  default(props: {
    disabled: boolean;
    index: number;
    item: TItem;
    itemAttributes: TKeyboardNavigableListItemAttributes;
    select: () => void;
    selected: boolean;
  }): unknown;
  footer?(): unknown;
}>();

const listRef = useTemplateRef<HTMLElement>('listRef');

const isItemDisabled = (item: TItem, index: number): boolean => props.isItemDisabled?.(item, index) ?? false;
const isItemSelected = (item: TItem, index: number): boolean =>
  Object.is(props.getValue(item, index), props.modelValue);

const tabbableItemIndex = computed<number>(() => {
  const selectedIndex = props.items.findIndex(
    (item, index) => !isItemDisabled(item, index) && isItemSelected(item, index),
  );
  if (selectedIndex >= 0) return selectedIndex;
  return props.items.findIndex((item, index) => !isItemDisabled(item, index));
});

const getItemAttributes = (item: TItem, index: number): TKeyboardNavigableListItemAttributes => {
  const disabled = isItemDisabled(item, index);
  return {
    ...(disabled ? { 'aria-disabled': 'true' as const } : {}),
    'data-keyboard-navigable-list-control': '',
    tabindex: !disabled && index === tabbableItemIndex.value ? 0 : -1,
  };
};

const selectItem = (item: TItem, index: number): void => {
  if (isItemDisabled(item, index)) return;
  emit('update:modelValue', props.getValue(item, index));
};

const isNavigationKey = (key: string): key is TKeyboardNavigableListKey =>
  key === EKeyboardKey.Down || key === EKeyboardKey.End || key === EKeyboardKey.Home || key === EKeyboardKey.Up;

const focusItem = async (index: number): Promise<void> => {
  await nextTick();
  const item = listRef.value?.querySelector<HTMLElement>(`[data-keyboard-navigable-list-index="${index}"]`);
  const control = item?.querySelector<HTMLElement>('[data-keyboard-navigable-list-control]');
  if (!control) return;
  control.focus({ preventScroll: true });
  control.scrollIntoView({ block: 'nearest', inline: 'nearest' });
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (
    !isNavigationKey(event.key) ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey ||
    !(event.target instanceof Element)
  ) {
    return;
  }

  const list = listRef.value;
  const currentControl = event.target.closest<HTMLElement>('[data-keyboard-navigable-list-control]');
  const currentItem = event.target.closest<HTMLElement>('[data-keyboard-navigable-list-index]');
  if (!list || !currentControl || !currentItem || !list.contains(currentItem)) return;
  if (currentControl.closest('[data-keyboard-navigable-list]') !== list) return;

  const currentIndex = Number(currentItem.dataset.keyboardNavigableListIndex);
  if (!Number.isInteger(currentIndex)) return;

  const targetIndex = getKeyboardNavigableListTargetIndex({
    currentIndex,
    isDisabled: (index) => {
      if (index < 0 || index >= props.items.length) return true;
      return isItemDisabled(props.items[index], index);
    },
    itemCount: props.items.length,
    key: event.key,
    wrap: props.wrap,
  });
  if (targetIndex == null) return;

  event.preventDefault();
  event.stopPropagation();

  if (targetIndex < 0 || targetIndex >= props.items.length) return;
  const targetItem = props.items[targetIndex];
  if (targetIndex !== currentIndex) selectItem(targetItem, targetIndex);
  void focusItem(targetIndex);
};
</script>

<template>
  <ul ref="listRef" data-keyboard-navigable-list @keydown="handleKeydown">
    <li
      v-for="(item, index) in props.items"
      :key="props.getKey(item, index)"
      :data-keyboard-navigable-list-index="index"
    >
      <slot
        :disabled="isItemDisabled(item, index)"
        :index="index"
        :item="item"
        :item-attributes="getItemAttributes(item, index)"
        :select="() => selectItem(item, index)"
        :selected="isItemSelected(item, index)"
      />
    </li>
    <slot name="footer" />
  </ul>
</template>
